import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useEffect, useState } from "react";
import CustomPagination from "../../../components/Common/CustomPagination";
import {
  customDateConvert,
  findTimeDifference,
} from "../../../components/Common/Utils/Utils";
import TableSkeleton from "./TableSkeleton";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#ECECEC",
    fontSize: "13px",
    color: "#4A4A4A",
    padding: "8px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "25px",
    "&:last-child": {
      color: "#A9A9A9",
    },
    paddingLeft: "8px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#F2F8FB",
  },
  // hide last border
  // "&:last-child td, &:last-child th": {
  //   border: 0,
  // },
  "td, th": {
    border: 0,
  },
  "& .css-1u9ya20-MuiTableCell-root": {
    borderBottom: "none",
  },
}));

const styles = {
  getButtonStyle: (prediction) => {
    let borderColor;
    let bgColor;
    if (prediction === "Good") {
      borderColor = "#58B55D";
      bgColor = "#E5F2E9";
    } else if (prediction === "Scrap") {
      borderColor = "#DD2020";
      bgColor = "#F9E8E5";
    } else {
      borderColor = "#EA8600";
      bgColor = "#F9EFE1";
    }
    return {
      padding: "5px",
      backgroundColor: bgColor,
      width: "105px",
      textAlign: "center",
      color: "#080808",
      fontSize: "13px",
      border: `2px solid ${borderColor}`,
      borderRadius: "5px",
    };
  },
};

function convertTime(input) {
  const parts = input.split(":");

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);

  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  const days = Math.floor(totalSeconds / (24 * 3600));
  const remainingHours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${days}Days ${remainingHours}Hrs ${remainingMinutes}Min ${remainingSeconds}Sec`;
}

export default function OpearatorPredictionTable({
  updatedTable,
  paginationData,
  pageData,
  setPageData,
  loading,
  isMaximized,
}) {
  const totalPages = paginationData.totalPages;
  const [currentTime, setCurrentTime] = useState(new Date());

  const handleChangePage = (newPage) => {
    setPageData((prevState) => ({
      ...prevState,
      currentPage: newPage,
    }));
  };

  const handleChangeItemsPerPage = (items) => {
    setPageData((prevState) => ({
      ...prevState,
      currentPage: 1,
      itemsPerPage: items,
    }));
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const formatTimeDifference = (exitTime) => {
    const exitDate = new Date(exitTime);
    const diffInSeconds = Math.floor((currentTime - exitDate) / 1000);

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <Paper>
      <TableContainer sx={{ maxHeight: isMaximized ? "85vh" : "74vh" }}>
        <Table
          stickyHeader
          sx={{
            minWidth: 700,
          }}
          aria-label="customized table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Timestamp</StyledTableCell>
              <StyledTableCell>Prediction</StyledTableCell>
              <StyledTableCell>Actual</StyledTableCell>
              <StyledTableCell>Part ID</StyledTableCell>
              <StyledTableCell>Highest Contributing Factor</StyledTableCell>
              <StyledTableCell>Off Paternoster Since</StyledTableCell>
            </TableRow>
          </TableHead>
          {loading ? (
            <tr>
              <td colSpan="6">
                <TableSkeleton />
              </td>
            </tr>
          ) : (
            <TableBody>
              {updatedTable?.length === 0 ? (
                <TableRow hover sx={{ cursor: "pointer" }}>
                  <TableCell scope="row" align="center" colSpan={5}>
                    <div
                      style={{
                        textAlign: "center",
                        color: "red",
                        fontSize: "20px",
                        width: "100%",
                      }}
                    >
                      No data available
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                updatedTable?.map((item, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      {customDateConvert(
                        item?.dryTunnelExitTime,
                        "MM/DD/YYYY HH:mm:ss"
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <div
                        style={styles.getButtonStyle(
                          item?.final_status || "NA"
                        )}
                      >
                        {item.final_status}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        fontSize: "13px",
                        color:
                          item?.actualStatus === "Good" ? "#34A853" : "#EA4335",
                      }}
                    >
                      {item?.actualStatus}
                    </StyledTableCell>
                    <StyledTableCell>{item?.partCode}</StyledTableCell>
                    <StyledTableCell>
                      {item?.highestContributingFactorName}{" "}
                      <span>
                        [ {item?.highestContributingFactorPercentage}% ]
                      </span>
                    </StyledTableCell>
                    <StyledTableCell>
                      {item.paternosterTimer === "on"
                        ? convertTime(
                            formatTimeDifference(item?.dryTunnelExitTime)
                          )
                        : findTimeDifference(
                            item.paternosterTimer,
                            item?.dryTunnelExitTime
                          )}
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {updatedTable?.length != 0 && (
        <CustomPagination
          currentPage={pageData.currentPage}
          totalPages={totalPages}
          changePage={handleChangePage}
          changeItemsPerPage={handleChangeItemsPerPage}
          minRows={10}
        />
      )}
    </Paper>
  );
}
